import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Hotel, Bus, Plane, Users, Search, Calendar, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { format } from 'date-fns';

interface AllocationConsumptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingItemId: string;
  bookingItemName: string;
  serviceDate?: Date;
  endDate?: Date;
  onSuccess?: () => void;
}

type AllocationType = 'HOTEL_ROOM' | 'TOUR_SEAT' | 'TRANSFER_VEHICLE' | 'FLIGHT_SEAT';

const typeIcons: Record<AllocationType, typeof Hotel> = {
  HOTEL_ROOM: Hotel,
  TOUR_SEAT: Users,
  TRANSFER_VEHICLE: Bus,
  FLIGHT_SEAT: Plane,
};

const typeColors: Record<AllocationType, string> = {
  HOTEL_ROOM: 'bg-blue-100 text-blue-700',
  TOUR_SEAT: 'bg-purple-100 text-purple-700',
  TRANSFER_VEHICLE: 'bg-green-100 text-green-700',
  FLIGHT_SEAT: 'bg-sky-100 text-sky-700',
};

export function AllocationConsumptionModal({
  open,
  onOpenChange,
  bookingItemId,
  bookingItemName,
  serviceDate,
  endDate,
  onSuccess,
}: AllocationConsumptionModalProps) {
  const { t } = useTranslation('allocations');
  const { t: tCommon } = useTranslation('common');
  const { t: tBookings } = useTranslation('bookings');

  const [search, setSearch] = useState('');
  const [selectedAllocation, setSelectedAllocation] = useState<string | null>(null);
  const [units, setUnits] = useState(1);
  const [customServiceDate, setCustomServiceDate] = useState(
    serviceDate ? format(serviceDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')
  );
  const [customEndDate, setCustomEndDate] = useState(
    endDate ? format(endDate, 'yyyy-MM-dd') : ''
  );
  const [notes, setNotes] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedAllocation(null);
      setUnits(1);
      setNotes('');
      setCustomServiceDate(serviceDate ? format(serviceDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'));
      setCustomEndDate(endDate ? format(endDate, 'yyyy-MM-dd') : '');
    }
  }, [open, serviceDate, endDate]);

  // Query available allocations
  const { data, isLoading } = trpc.allocation.list.useQuery({
    page: 1,
    search: search || undefined,
    status: 'ACTIVE',
    hasAvailability: true,
    validFrom: new Date(customServiceDate),
  }, {
    enabled: open,
  });

  const consumeMutation = trpc.allocation.consume.useMutation({
    onSuccess: () => {
      showSuccessToast(t('messages.consumed') || 'Allocation consumed successfully');
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleSubmit = () => {
    if (!selectedAllocation) return;

    consumeMutation.mutate({
      allocationId: selectedAllocation,
      bookingItemId,
      units,
      serviceDate: new Date(customServiceDate),
      endDate: customEndDate ? new Date(customEndDate) : undefined,
      notes: notes || undefined,
    });
  };

  const allocations = data?.allocations ?? [];
  const selectedAllocationData = allocations.find(a => a.id === selectedAllocation);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tBookings('allocations.selectAllocation') || 'Select Allocation'}</DialogTitle>
          <DialogDescription>
            {tBookings('allocations.selectDescription') || `Choose an allocation to consume for "${bookingItemName}"`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Date Selection */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>{t('fields.serviceDate') || 'Service Date'}</Label>
              <Input
                type="date"
                value={customServiceDate}
                onChange={(e) => setCustomServiceDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('fields.endDate') || 'End Date (optional)'}</Label>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">{t('loading')}</span>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && allocations.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">
                {t('empty.noAllocations') || 'No available allocations found for this date'}
              </p>
            </div>
          )}

          {/* Allocations List */}
          {!isLoading && allocations.length > 0 && (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {allocations.map((allocation) => {
                const Icon = typeIcons[allocation.allocationType as AllocationType];
                const isSelected = selectedAllocation === allocation.id;

                return (
                  <div
                    key={allocation.id}
                    onClick={() => setSelectedAllocation(allocation.id)}
                    className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${typeColors[allocation.allocationType as AllocationType]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{allocation.name}</p>
                      <p className="text-sm text-muted-foreground">{allocation.partner.companyName}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-600">
                        {allocation.availableUnits} {t('fields.available') || 'available'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: allocation.currency,
                        }).format(allocation.unitCost)}
                        {' '}/{' '}{tCommon('unit') || 'unit'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Selected Allocation Details */}
          {selectedAllocationData && (
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('fields.selected') || 'Selected'}</span>
                <span className="font-medium">{selectedAllocationData.name}</span>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="units">{t('fields.units') || 'Units to consume'}</Label>
                  <Input
                    id="units"
                    type="number"
                    min={1}
                    max={selectedAllocationData.availableUnits}
                    value={units}
                    onChange={(e) => setUnits(Math.min(Number(e.target.value), selectedAllocationData.availableUnits))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('fields.totalCost') || 'Total Cost'}</Label>
                  <p className="text-lg font-semibold">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: selectedAllocationData.currency,
                    }).format(selectedAllocationData.unitCost * units)}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">{t('fields.notes') || 'Notes'}</Label>
                <Input
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={tBookings('allocations.notesPlaceholder') || 'Optional notes...'}
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>
                  {format(new Date(selectedAllocationData.validFrom), 'MMM d, yyyy')} -{' '}
                  {format(new Date(selectedAllocationData.validTo), 'MMM d, yyyy')}
                </span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedAllocation || consumeMutation.isPending}
          >
            {consumeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {tBookings('allocations.consume') || 'Consume Allocation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
